import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Forward, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { useSession } from "@/context/Session";
import { API, errMsg } from "@/lib/api";
import { getCompany } from "@/lib/api";
import { Notice, PageHeader } from "@/components/app/ui";

const CHIPS = ["Resuma minhas pendências e o que fazer agora", "Quais impostos e obrigações minha empresa tem?", "Como funciona o Simples Nacional e o Fator R?",
  "Como envio uma nota fiscal já emitida?", "Qual o status dos meus chamados?", "Como abro um chamado de suporte?"];
const md = (t) => (t || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <b key={i}>{p.slice(2, -2)}</b> : p.replace(/^#{1,4}\s/gm, "").replace(/^\s*[-*]\s/gm, "• ")));

export default function Assistant() {
  const { api } = useSession();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const sid = useRef(localStorage.getItem(`normah_ai_${getCompany()}`) || "");
  const end = useRef();
  useEffect(() => {
    api.get("/ai/usage").then((r) => setUsage(r.data)).catch(() => {});
    if (sid.current) api.get("/ai/history", { params: { session_id: sid.current } }).then((r) => setMsgs(r.data.map((m) => ({ role: m.role, text: m.text })))).catch(() => {});
  }, [api]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (q) => {
    const message = (q || text).trim(); if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      const token = await api.token();
      const res = await fetch(`${API}/ai/chat`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "X-Company-Id": getCompany() }, body: JSON.stringify({ message, session_id: sid.current || undefined }) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw { response: { data: j } }; }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const raw = p.replace(/^data: /, ""); if (raw === "[DONE]") continue;
          const ev = JSON.parse(raw);
          if (ev.session_id) { sid.current = ev.session_id; localStorage.setItem(`normah_ai_${getCompany()}`, ev.session_id); }
          if (ev.t || ev.error) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + (ev.t || ev.error) }; return c; });
        }
      }
      api.get("/ai/usage").then((r) => setUsage(r.data));
    } catch (e) { toast.error(errMsg(e)); setMsgs((m) => m.slice(0, -1)); } finally { setBusy(false); }
  };

  const forward = async (t, category = "contador") => {
    try { await api.post("/tickets", { subject: category === "contador" ? "Dúvida encaminhada pela Normah AI" : "Ajuda solicitada pela Normah AI", category, priority: "media", description: t.slice(0, 1500) }); toast.success(category === "contador" ? "Encaminhado ao contador em Meu Contador" : "Chamado aberto em Suporte"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (<div data-testid="assistant-page">
    <PageHeader title="Normah AI" subtitle="Tire dúvidas de contabilidade, impostos e obrigações, aprenda a usar o sistema e acompanhe seus chamados.">
      {usage && <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold" data-testid="ai-usage">{usage.used}/{usage.limit} mensagens no mês</span>}
    </PageHeader>
    <Notice>A Normah AI não substitui o contador, não confirma cálculos definitivos e não transmite obrigações. Dados sensíveis (CNPJ, valores, documentos) não são enviados ao provedor de IA.</Notice>
    <div className="card-soft flex h-[62vh] flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {msgs.length === 0 && <div className="flex h-full flex-col items-center justify-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#12343B]"><Sparkles className="h-6 w-6 text-[#81D8D0]" /></span>
          <p className="mt-3 font-bold">Como posso ajudar?</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">{CHIPS.map((c) => <button key={c} onClick={() => ask(c)} data-testid="ai-chip" className="rounded-full border border-[#DFE6E9] bg-white px-3 py-1.5 text-sm hover:bg-[#E3FAF7]">{c}</button>)}</div>
        </div>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-[#12343B] text-white" : "bg-[#F4F6F8]"}`} data-testid={`ai-msg-${m.role}`}>
              <p className="whitespace-pre-wrap">{m.role === "assistant" ? md(m.text) : m.text}{!m.text && busy && i === msgs.length - 1 ? "Pensando..." : ""}</p>
              {m.role === "assistant" && m.text && !busy && <div className="mt-2 flex flex-wrap gap-3">
                <button onClick={() => forward(`${msgs[i - 1]?.text}\n\nResumo da IA: ${m.text}`)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#2a8f86]" data-testid="ai-forward-accountant"><Forward className="h-3 w-3" />Encaminhar ao contador</button>
                <button onClick={() => forward(`${msgs[i - 1]?.text}\n\nResumo da IA: ${m.text}`, "plataforma")} className="inline-flex items-center gap-1 text-xs font-semibold text-[#2a8f86]" data-testid="ai-open-ticket"><LifeBuoy className="h-3 w-3" />Abrir chamado de suporte</button></div>}
            </div>
          </div>))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 border-t p-3">
        <input data-testid="ai-input" className="field" value={text} onChange={(e) => setText(e.target.value)} placeholder="Pergunte à Normah AI..." />
        <button className="btn-primary w-12 px-0" disabled={busy} data-testid="ai-send" aria-label="Enviar"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  </div>);
}
